import type { TFunction } from 'i18next';

import type { IconVariant } from '../components/Icon';
import type {
  FilterWidth,
  GroupOptions,
} from '../components/Filter/filter-types';

import type { HeatMapRange } from './map-data-types';

export interface HeatMapFilterDefinition {
  key: string;
  icon: IconVariant;
  titleKey: string;
  filterType: 'multiChoice' | 'multiChoiceToggle' | 'select' | 'range';
  viewModes?: readonly HeatMapRange[];
  width?: FilterWidth;
  clearValue?: string;
}

export const heatmapFilterRegistry = {
  continent: {
    key: 'continent',
    icon: 'Globe',
    titleKey: 'heatMapExperience.filters.titles.region',
    filterType: 'select',
    width: 'full',
  },
  status: {
    key: 'status',
    icon: 'StudyDesignCheck',
    titleKey: 'heatMapExperience.filters.titles.status',
    filterType: 'multiChoiceToggle',
    viewModes: ['incidence', 'mortality'],
    width: 'full',
    clearValue: 'AllStatuses',
  },
  nccpAvailable: {
    key: 'nccpAvailable',
    icon: 'FactCheck',
    titleKey: 'heatMapExperience.filters.titles.hasNccp',
    filterType: 'multiChoice',
    viewModes: ['incidence', 'mortality', 'status'],
  },
  lungCancerInNccp: {
    key: 'lungCancerInNccp',
    icon: 'Lungs',
    titleKey: 'heatMapExperience.filters.titles.lungCancerInNccp',
    filterType: 'multiChoice',
    viewModes: ['incidence', 'mortality', 'status'],
  },
  earlyDetectionInNccp: {
    key: 'earlyDetectionInNccp',
    icon: 'EarlyDetection',
    titleKey: 'heatMapExperience.filters.titles.earlyDetectionInNccp',
    filterType: 'multiChoice',
    viewModes: ['incidence', 'mortality', 'status'],
  },
  fundingForEarlyDetection: {
    key: 'fundingForEarlyDetection',
    icon: 'Funding',
    titleKey: 'heatMapExperience.filters.titles.fundingForEarlyDetection',
    filterType: 'multiChoice',
    viewModes: ['incidence', 'mortality', 'status'],
  },
  nccpYearMax: {
    key: 'nccpYearMax',
    icon: 'DateRange',
    titleKey: 'heatMapExperience.filters.titles.nccpYear',
    filterType: 'range',
    viewModes: ['nccp'],
  },
  statusYearMax: {
    key: 'statusYearMax',
    icon: 'DateRange',
    titleKey: 'heatMapExperience.filters.titles.statusYear',
    filterType: 'range',
    viewModes: ['status'],
  },
} as const satisfies Record<string, HeatMapFilterDefinition>;

export type HeatMapFilterKey = keyof typeof heatmapFilterRegistry;

// Option builders

export const buildYesNoOptions = (t: TFunction): GroupOptions[] => [
  { label: t('poiExperience.filters.options.seeAll'), value: undefined },
  { value: 'Yes', label: t('poiExperience.filters.options.yes') },
  { value: 'No', label: t('poiExperience.filters.options.no') },
];

// View mode helpers (used by NavigationBar and FilteredHeatMapDataProvider)

export const filterAppliesToViewMode = (
  key: string,
  viewMode: HeatMapRange,
): boolean => {
  const def = heatmapFilterRegistry[
    key as HeatMapFilterKey
  ] as HeatMapFilterDefinition;
  if (!def?.viewModes) return true;
  return def.viewModes.includes(viewMode);
};
